/**
 * 屏 7 — 地图编辑器（V2 新增）
 *
 * 功能：
 *   - 15×15 / 25×25 / 32×32 地图编辑
 *   - 上下 + 左右对称镜像（默认 ON，可独立关闭）
 *   - 4 种 tile：空地（E 0x00）/ cover 掩体（C 0x55）/ obstacle 障碍（O 0xAA）/ unreachable 不可达（U 0xFF）
 *   - spawn 点编辑（A/B 永远左右镜像）
 *   - 保存走 LevelStore（DevLevelStore / OPFSLevelStore）
 *   - 导出 .level.zip 下载
 *   - 撤销/重做（最多 20 步）
 *
 * Ardot 战术美学 v2.0：
 *   - 背景 #0A0A0F，网格线 rgba(255,255,255,0.06)
 *   - 4px 色条、Mono 字体、[bracket] 标注、crosshair 光标
 *   - 锐角（border-radius: 0）、无过渡动画
 *
 * 参考文件：
 *   - design/v2/v2-002-map-editor.md（功能规格）
 *   - design/v2/v2-006-map-editor-ux.md（Dale UX 规范）
 *   - prototype/src/render/canvas-renderer.ts（CanvasRenderer 类，屏 4 复用）
 *   - prototype/src/storage/level-store.ts（LevelStore 双实现）
 */

import { getLevelStore, type LevelStore, type LevelData, downloadLevelZip } from "../storage/level-store";
import { redirectProjectLevel } from "../storage/project-store";
import { pathFor } from "../router/screen-router";
import { CanvasRenderer } from "../render/canvas-renderer";
import { TILE_NUM } from "../engine/tile-codec";
import { TOKEN_MAP, tokenColorRgba } from "../utils/color-tokens";
import { t } from "../i18n";
import { isLoggedIn } from "../core/user-context";

// ============================================================
// 常量
// ============================================================

/** 可选地图尺寸 */
const MAP_SIZES = [15, 25, 32] as const;
type MapSize = typeof MAP_SIZES[number];
const DEFAULT_SIZE: MapSize = 25;
const screen7DialogEscCleanups = new Set<() => void>();

/** Tile 值（统一来源 → tile-codec.ts TILE_NUM，单一真相源） */
const TILE_EMPTY = TILE_NUM.EMPTY;
const TILE_COVER = TILE_NUM.COVER;
const TILE_OBSTACLE = TILE_NUM.OBSTACLE;
const TILE_UNREACHABLE = TILE_NUM.UNREACHABLE;

/** Tile 工具类型 */
type TileTool = "empty" | "cover" | "obstacle" | "unreachable";
type EditorTool = TileTool | "spawn" | "select" | "clear";

/** Spawn 坐标 */
interface SpawnPoint {
  x: number;
  y: number;
}

/** 编辑状态 */
interface EditorState {
  mapSize: MapSize;
  tiles: number[][];           // tiles[y][x]
  spawnA: SpawnPoint | null;
  spawnB: SpawnPoint | null;
  activeTool: EditorTool;
  hMirror: boolean;
  vMirror: boolean;
  /** 关卡元数据（从 LevelStore 加载或新建） */
  levelId: string;
  levelName: string;
  difficulty: "easy" | "medium" | "hard";
  description: string;
  aiName: string;
  isBuiltin: boolean;
  /** 原始数据（用于检测是否有修改） */
  originalTiles: number[][] | null;
  originalSpawnA: SpawnPoint | null;
  originalSpawnB: SpawnPoint | null;
}

/** 撤销快照 */
interface UndoSnapshot {
  tiles: number[][];
  spawnA: SpawnPoint | null;
  spawnB: SpawnPoint | null;
}

// ============================================================
// 主渲染函数
// ============================================================

export async function renderScreen7MapEditor(
  root: HTMLElement,
  levelId: string,
  onNavigate: (path: string) => void,
  isActive: () => boolean = () => true,
): Promise<void> {
  // ---- 状态初始化 ----
  if (!isLoggedIn() && (!levelId || levelId === "new")) {
    root.innerHTML = `
      <div class="s7-auth-required">
        <h2>AUTH REQUIRED</h2>
        <p>创建和保存用户关卡需要登录账号。</p>
        <button type="button" id="s7-auth-login">返回首页登录</button>
      </div>`;
    root.querySelector("#s7-auth-login")?.addEventListener("click", () => onNavigate("#/"));
    return;
  }
  const levelStore = getLevelStore();
  const state: EditorState = {
    mapSize: DEFAULT_SIZE,
    tiles: [],
    spawnA: null,
    spawnB: null,
    activeTool: "empty",
    hMirror: true,
    vMirror: true,
    levelId,
    levelName: "Untitled Map",
    difficulty: "easy",
    description: "",
    aiName: "Custom AI",
    isBuiltin: false,
    originalTiles: null,
    originalSpawnA: null,
    originalSpawnB: null,
  };

  const undoStack: UndoSnapshot[] = [];
  const redoStack: UndoSnapshot[] = [];
  const MAX_UNDO = 20;

  let canvas: HTMLCanvasElement | null = null;
  let canvasRenderer: CanvasRenderer | null = null;
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragCurrentX = 0;
  let dragCurrentY = 0;
  let hoverTileX = -1;
  let hoverTileY = -1;

  // ---- 初始化地图数据 ----
  initEmptyMap(state);

  // ---- 尝试从 LevelStore 加载已有关卡 ----
  if (levelId && levelId !== "new") {
    try {
      const existing = await levelStore.load(levelId);
      if (!isActive()) return;
      applyLoadedLevel(state, existing);
      // 标记原始数据（用于检测修改）
      state.originalTiles = state.tiles.map(row => [...row]);
      state.originalSpawnA = state.spawnA ? { ...state.spawnA } : null;
      state.originalSpawnB = state.spawnB ? { ...state.spawnB } : null;
    } catch {
      // 加载失败，使用空地图（levelId 可能是"new"或其他）
      console.warn(`[屏 7] 无法加载关卡 ${levelId}，使用空地图`);
      state.originalTiles = state.tiles.map(row => [...row]);
      state.originalSpawnA = null;
      state.originalSpawnB = null;
    }
  } else {
    state.originalTiles = state.tiles.map(row => [...row]);
    state.originalSpawnA = null;
    state.originalSpawnB = null;
  }

  // ---- 构建 HTML ----
  if (!isActive()) return;
  root.innerHTML = buildHtml(state, levelStore);

  const cleanupFns: Array<() => void> = [];
  let disposed = false;
  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    cleanupFns.splice(0).forEach((fn) => fn());
    Array.from(screen7DialogEscCleanups).forEach((fn) => fn());
    root.querySelectorAll(".s7-dialog-overlay").forEach((dialog) => dialog.remove());
    (root as any)._s7Cleanup = undefined;
  };
  (root as any)._s7Cleanup = cleanup;

  // ---- 绑定 DOM 引用 ----
  root.querySelector("#s7-editor") as HTMLElement;
  canvas = root.querySelector("#s7-canvas") as HTMLCanvasElement;
  const topBarStatusEl = root.querySelector("#s7-topbar-status") as HTMLElement;
  const panelEl = root.querySelector("#s7-panel") as HTMLElement;
  const panelToggleEl = root.querySelector("#s7-panel-toggle") as HTMLElement;
  const tileCountsEl = root.querySelector("#s7-tile-counts") as HTMLElement;
  const saveBtn = root.querySelector("#s7-btn-save") as HTMLButtonElement;
  const exportBtn = root.querySelector("#s7-btn-export") as HTMLButtonElement;
  const undoBtn = root.querySelector("#s7-btn-undo") as HTMLButtonElement;
  const clearBtn = root.querySelector("#s7-btn-clear") as HTMLButtonElement;
  const mirrorHEl = root.querySelector("#s7-mirror-h") as HTMLElement;
  const mirrorVEl = root.querySelector("#s7-mirror-v") as HTMLElement;
  const sizeSelectEl = root.querySelector("#s7-size-select") as HTMLSelectElement;
  const toastContainer = root.querySelector("#s7-toast-container") as HTMLElement;

  // ---- Canvas 设置 ----
  if (canvas) {
    const cellSize = calcCellSize(state.mapSize);
    canvas.width = state.mapSize * cellSize;
    canvas.height = state.mapSize * cellSize;
    canvas.style.cursor = "crosshair";

    canvasRenderer = new CanvasRenderer(canvas, {
      cellSize,
      showGrid: true,
      showHpBar: false,
      showSpawns: true,
    });

    // 渲染初始地图
    renderEditorCanvas(canvasRenderer, state, cellSize, hoverTileX, hoverTileY, dragStartX, dragStartY, dragCurrentX, dragCurrentY, isDragging);
  }

  // ---- 事件绑定 ----
  bindToolButtons(root, state, canvas);
  bindCanvasEvents(root, state, canvas, canvasRenderer, undoStack, redoStack, MAX_UNDO, tileCountsEl, topBarStatusEl);
  bindPanelEvents(root, state, panelEl, panelToggleEl, levelStore, tileCountsEl, saveBtn, exportBtn, undoBtn, clearBtn, undoStack, redoStack, MAX_UNDO, topBarStatusEl, toastContainer, onNavigate);
  bindTopBarEvents(root, state, canvas, canvasRenderer, mirrorHEl, mirrorVEl, sizeSelectEl, undoStack, redoStack, toastContainer, topBarStatusEl, tileCountsEl);
  cleanupFns.push(bindKeyboardEvents(root, state, canvas, canvasRenderer, undoStack, redoStack, MAX_UNDO, tileCountsEl, topBarStatusEl, levelStore, toastContainer, saveBtn, onNavigate));

  // ---- 初始更新 ----
  updateTileCounts(state, tileCountsEl);
  updateUndoCount(undoBtn, undoStack.length, MAX_UNDO);
  updateMirrorUI(mirrorHEl, mirrorVEl, state);
  updateTopBarStatus(topBarStatusEl, "EDITING");
}

// ============================================================
// 地图初始化
// ============================================================

function initEmptyMap(state: EditorState): void {
  const { mapSize } = state;
  state.tiles = [];
  for (let y = 0; y < mapSize; y++) {
    state.tiles[y] = new Array(mapSize).fill(TILE_EMPTY);
  }
  state.spawnA = null;
  state.spawnB = null;
}

/** 从 LevelData 加载地图 */
function applyLoadedLevel(state: EditorState, level: LevelData): void {
  state.levelId = level.levelId;
  state.levelName = level.name;
  state.difficulty = level.difficulty as "easy" | "medium" | "hard";
  state.description = level.description;
  state.aiName = level.aiName;
  state.isBuiltin = level.isBuiltin;

  const map = level.map;
  state.mapSize = (map.width as MapSize) ?? DEFAULT_SIZE;
  state.tiles = map.tiles.map(row => [...row]);

  if (map.spawnA && Array.isArray(map.spawnA)) {
    state.spawnA = { x: map.spawnA[0], y: map.spawnA[1] };
  }
  if (map.spawnB && Array.isArray(map.spawnB)) {
    state.spawnB = { x: map.spawnB[0], y: map.spawnB[1] };
  }
}

// ============================================================
// HTML 构建
// ============================================================

function buildHtml(state: EditorState, levelStore: LevelStore): string {
  const cellSize = calcCellSize(state.mapSize);
  const canvasW = state.mapSize * cellSize;
  const canvasH = state.mapSize * cellSize;
  const storagePath = getStoragePath(levelStore, state.levelId);

  return /*html*/`
<div id="s7-editor" class="s7-editor">
  <!-- Toast 容器 -->
  <div id="s7-toast-container" class="s7-toast-container"></div>

  <!-- 顶部 Toolbar -->
  <div class="s7-topbar">
    <div class="s7-topbar-left">
      <button class="s7-btn s7-btn-back" id="s7-btn-back">← ${t("common.back")}</button>
      <span class="s7-topbar-title">OP-01 · ${t("map_editor.title")} · <span id="s7-topbar-status" class="s7-status-tag">[${t("map_editor.editing")}]</span></span>
    </div>
    <div class="s7-topbar-right">
      <div class="s7-topbar-group">
        <span class="s7-label">SIZE:</span>
        <select id="s7-size-select" class="s7-select">
          ${MAP_SIZES.map(s => `<option value="${s}" ${s === state.mapSize ? "selected" : ""}>${s}×${s}</option>`).join("")}
        </select>
      </div>
      <div class="s7-topbar-group">
        <span class="s7-label">MIRROR:</span>
        <button id="s7-mirror-h" class="s7-mirror-btn ${state.hMirror ? "on" : "off"}">[⇅ ${state.hMirror ? "ON" : "OFF"}]</button>
        <button id="s7-mirror-v" class="s7-mirror-btn ${state.vMirror ? "on" : "off"}">[⇄ ${state.vMirror ? "ON" : "OFF"}]</button>
      </div>
    </div>
  </div>

  <!-- 主区域 -->
  <div class="s7-main">
    <!-- 左侧工具条 -->
    <div class="s7-toolbar" id="s7-toolbar">
      ${buildToolButtons(state.activeTool)}
    </div>

    <!-- 画布区域 -->
    <div class="s7-canvas-area">
      <div class="s7-coords s7-coords-top" id="s7-coords-top">
        ${buildCoordLabels(state.mapSize)}
      </div>
      <div class="s7-canvas-wrapper">
        <div class="s7-coords s7-coords-left" id="s7-coords-left">
          ${buildCoordLabelsY(state.mapSize)}
        </div>
        <canvas id="s7-canvas" width="${canvasW}" height="${canvasH}"></canvas>
        <div class="s7-coords s7-coords-right" id="s7-coords-right">
          ${buildCoordLabelsY(state.mapSize)}
        </div>
      </div>
      <div class="s7-coords s7-coords-bottom" id="s7-coords-bottom">
        ${buildCoordLabels(state.mapSize)}
      </div>
      <div class="s7-legend">
        ▣=E(Empty) ▤=C(Cover) ▥=O(Obstacle) ▩=U(Unreachable) ◆=Spawn ┊=Mirror
      </div>
    </div>

    <!-- 右侧属性面板 -->
    <div class="s7-panel" id="s7-panel">
      <button class="s7-panel-toggle" id="s7-panel-toggle">[◀]</button>
      <div class="s7-panel-content">
        <!-- LEVEL INFO -->
        <div class="s7-panel-section">
          <div class="s7-panel-title">LEVEL INFO</div>
          <div class="s7-field">
            <label class="s7-field-label">Name:</label>
            <input id="s7-input-name" class="s7-input" type="text" value="${escapeHtml(state.levelName)}" maxlength="32" />
          </div>
          <div class="s7-field">
            <label class="s7-field-label">Desc:</label>
            <textarea id="s7-input-desc" class="s7-input s7-textarea" rows="3" maxlength="256">${escapeHtml(state.description)}</textarea>
          </div>
          <div class="s7-field">
            <label class="s7-field-label">Difficulty:</label>
            <select id="s7-select-difficulty" class="s7-select">
              <option value="easy" ${state.difficulty === "easy" ? "selected" : ""}>Easy</option>
              <option value="medium" ${state.difficulty === "medium" ? "selected" : ""}>Medium</option>
              <option value="hard" ${state.difficulty === "hard" ? "selected" : ""}>Hard</option>
            </select>
          </div>
        </div>

        <!-- TILE COUNTS -->
        <div class="s7-panel-section">
          <div class="s7-panel-title">TILE COUNTS</div>
          <div id="s7-tile-counts" class="s7-tile-counts"></div>
        </div>

        <!-- ACTIONS -->
        <div class="s7-panel-section">
          <div class="s7-panel-title">ACTIONS</div>
          <button id="s7-btn-save" class="s7-action-btn s7-btn-save">${state.isBuiltin ? `[🔀 ${t("map_editor.save_as")}]` : `[💾 ${t("map_editor.save")}]`}</button>
          <button id="s7-btn-export" class="s7-action-btn s7-btn-export">[📦 ${t("map_editor.export")} .level.zip]</button>
          <button id="s7-btn-undo" class="s7-action-btn s7-btn-undo">[↶ ${t("map_editor.undo")} 0/20]</button>
          <button id="s7-btn-clear" class="s7-action-btn s7-btn-clear s7-btn-danger">[⊘ ${t("map_editor.clear_all")}]</button>
          <div class="s7-panel-meta">
            <div class="s7-meta-line">PATH: <span class="s7-meta-value">${escapeHtml(storagePath)}</span></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`;
}

function buildToolButtons(activeTool: EditorTool): string {
  const tools: Array<{ id: EditorTool; label: string; key: string; icon: string }> = [
    { id: "empty", label: "Empty", key: "1", icon: "▣" },
    { id: "cover", label: "Cover", key: "2", icon: "▤" },
    { id: "obstacle", label: "Obstacle", key: "3", icon: "▥" },
    { id: "unreachable", label: "Unreachable", key: "4", icon: "▩" },
  ];

  const spawnTools: Array<{ id: EditorTool; label: string; key: string; icon: string }> = [
    { id: "spawn", label: t("map_editor.spawn_p1"), key: "5", icon: "⛳" },
    { id: "spawn", label: t("map_editor.spawn_p2"), key: "6", icon: "⛳" },
  ];

  const miscTools: Array<{ id: EditorTool; label: string; key: string; icon: string }> = [
    { id: "select", label: "Select", key: "7", icon: "⌖" },
    { id: "clear", label: t("map_editor.clear_all"), key: "8", icon: "⊘" },
  ];

  let html = "";

  for (const t of tools) {
    const active = activeTool === t.id ? "active" : "";
    html += `<button class="s7-tool-btn ${active}" data-tool="${t.id}" title="${t.label} [${t.key}]">
      <span class="s7-tool-icon">${t.icon}</span>
      <span class="s7-tool-label">${t.label}</span>
      <span class="s7-tool-key">[${t.key}]</span>
    </button>`;
  }

  html += '<div class="s7-tool-sep"></div>';

  for (const t of spawnTools) {
    const active = activeTool === "spawn" ? "active" : "";
    html += `<button class="s7-tool-btn ${active}" data-tool="spawn" title="${t.label} [${t.key}]">
      <span class="s7-tool-icon">${t.icon}</span>
      <span class="s7-tool-label">${t.label}</span>
      <span class="s7-tool-key">[${t.key}]</span>
    </button>`;
  }

  html += '<div class="s7-tool-sep"></div>';

  for (const t of miscTools) {
    const active = activeTool === t.id ? "active" : "";
    html += `<button class="s7-tool-btn ${active}" data-tool="${t.id}" title="${t.label} [${t.key}]">
      <span class="s7-tool-icon">${t.icon}</span>
      <span class="s7-tool-label">${t.label}</span>
      <span class="s7-tool-key">[${t.key}]</span>
    </button>`;
  }

  return html;
}

function buildCoordLabels(size: number): string {
  let html = "";
  for (let i = 0; i < size; i++) {
    if (i % 5 === 0) {
      html += `<span class="s7-coord-label">[${String(i).padStart(2, "0")}]</span>`;
    } else {
      html += `<span class="s7-coord-spacer"></span>`;
    }
  }
  return html;
}

function buildCoordLabelsY(size: number): string {
  let html = "";
  for (let i = 0; i < size; i++) {
    if (i % 5 === 0) {
      html += `<span class="s7-coord-label-y">[${String(i).padStart(2, "0")}]</span>`;
    } else {
      html += `<span class="s7-coord-spacer-y"></span>`;
    }
  }
  return html;
}

// ============================================================
// Canvas 渲染（编辑器模式）
// ============================================================

function calcCellSize(mapSize: number): number {
  // canvas 最大区域约 800px（适配面板后），减去坐标标注空间
  const maxCanvasWidth = 800;
  return Math.max(20, Math.floor(maxCanvasWidth / mapSize));
}

function renderEditorCanvas(
  renderer: CanvasRenderer,
  state: EditorState,
  cellSize: number,
  hoverX: number,
  hoverY: number,
  dragX1: number,
  dragY1: number,
  dragX2: number,
  dragY2: number,
  isDragging: boolean,
): void {
  const canvas = (renderer as any).ctx?.canvas as HTMLCanvasElement;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const { mapSize, tiles, spawnA, spawnB, hMirror, vMirror } = state;
  const cell = cellSize;
  const W = mapSize * cell;
  const H = mapSize * cell;

  // 1. 背景（V14 地图提亮）
  ctx.fillStyle = TOKEN_MAP.mapBg();
  ctx.fillRect(0, 0, W, H);

  // 2. 绘制 tile
  for (let y = 0; y < mapSize; y++) {
    for (let x = 0; x < mapSize; x++) {
      drawEditorTile(ctx, x, y, tiles[y][x], cell);
    }
  }

  // 3. 网格线
  drawEditorGrid(ctx, mapSize, cell);

  // 4. 镜像中线
  drawMirrorLines(ctx, mapSize, cell, hMirror, vMirror);

  // 5. Spawn 标记
  if (spawnA) drawSpawnMarker(ctx, spawnA.x, spawnA.y, cell, TOKEN_MAP.cyan());
  if (spawnB) drawSpawnMarker(ctx, spawnB.x, spawnB.y, cell, TOKEN_MAP.red());

  // 6. 拖拽预览（刷子矩形）
  if (isDragging) {
    drawDragPreview(ctx, dragX1, dragY1, dragX2, dragY2, cell);
  } else if (hoverX >= 0 && hoverY >= 0 && hoverX < mapSize && hoverY < mapSize) {
    // hover 预览
    drawHoverPreview(ctx, hoverX, hoverY, cell, state);
  }
}

function drawEditorTile(ctx: CanvasRenderingContext2D, x: number, y: number, tile: number, cell: number): void {
  const px = x * cell;
  const py = y * cell;

  switch (tile) {
    case TILE_EMPTY:
      ctx.fillStyle = TOKEN_MAP.mapEmpty();
      ctx.fillRect(px, py, cell, cell);
      break;
    case TILE_COVER:
      ctx.fillStyle = TOKEN_MAP.mapCover();
      ctx.fillRect(px, py, cell, cell);
      // 掩体描边(亮蓝)
      ctx.strokeStyle = TOKEN_MAP.mapCoverStroke();
      ctx.lineWidth = 1.5;
      ctx.strokeRect(px + 0.5, py + 0.5, cell - 1, cell - 1);
      // 左上角小三角（与屏 4 一致，用战术青）
      ctx.fillStyle = TOKEN_MAP.cyan();
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + cell / 3, py);
      ctx.lineTo(px, py + cell / 3);
      ctx.fill();
      ctx.globalAlpha = 1;
      break;
    case TILE_OBSTACLE:
      ctx.fillStyle = TOKEN_MAP.mapObstacle();
      ctx.fillRect(px, py, cell, cell);
      // 障碍描边(浅红)
      ctx.strokeStyle = TOKEN_MAP.mapObstacleStroke();
      ctx.lineWidth = 1.5;
      ctx.strokeRect(px + 0.5, py + 0.5, cell - 1, cell - 1);
      // 对角十字线
      ctx.beginPath();
      ctx.moveTo(px + cell * 0.25, py + cell * 0.25);
      ctx.lineTo(px + cell * 0.75, py + cell * 0.75);
      ctx.moveTo(px + cell * 0.75, py + cell * 0.25);
      ctx.lineTo(px + cell * 0.25, py + cell * 0.75);
      ctx.stroke();
      break;
    case TILE_UNREACHABLE:
      ctx.fillStyle = TOKEN_MAP.mapUnreachable();
      ctx.fillRect(px, py, cell, cell);
      // 条纹(红色描边)
      ctx.strokeStyle = TOKEN_MAP.mapObstacleStroke();
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      for (let i = 0; i < cell; i += 4) {
        ctx.beginPath();
        ctx.moveTo(px + i, py);
        ctx.lineTo(px + i, py + cell);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      break;
  }
}

function drawEditorGrid(ctx: CanvasRenderingContext2D, mapSize: number, cell: number): void {
  const W = mapSize * cell;
  const H = mapSize * cell;

  // V14 地图提亮: 细网格（可见浅灰）
  ctx.strokeStyle = TOKEN_MAP.mapGridFine();
  ctx.lineWidth = 0.5;
  for (let i = 0; i <= mapSize; i++) {
    ctx.beginPath();
    ctx.moveTo(i * cell, 0);
    ctx.lineTo(i * cell, H);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, i * cell);
    ctx.lineTo(W, i * cell);
    ctx.stroke();
  }

  // V14 地图提亮: 主网格线（每 5 格加粗，更可见）
  ctx.strokeStyle = TOKEN_MAP.mapGridMajor();
  ctx.lineWidth = 1.5;
  for (let i = 0; i <= mapSize; i++) {
    if (i % 5 === 0) {
      ctx.beginPath();
      ctx.moveTo(i * cell, 0);
      ctx.lineTo(i * cell, H);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * cell);
      ctx.lineTo(W, i * cell);
      ctx.stroke();
    }
  }
}

function drawMirrorLines(ctx: CanvasRenderingContext2D, mapSize: number, cell: number, hMirror: boolean, vMirror: boolean): void {
  ctx.strokeStyle = TOKEN_MAP.cyan();
  ctx.lineWidth = 1.5;
  ctx.setLineDash([6, 4]);

  const W = mapSize * cell;
  const H = mapSize * cell;

  // 垂直中线（左右镜像）
  if (hMirror) {
    const midX = Math.floor(mapSize / 2) * cell;
    ctx.beginPath();
    ctx.moveTo(midX, 0);
    ctx.lineTo(midX, H);
    ctx.stroke();
  }

  // 水平中线（上下镜像）
  if (vMirror) {
    const midY = Math.floor(mapSize / 2) * cell;
    ctx.beginPath();
    ctx.moveTo(0, midY);
    ctx.lineTo(W, midY);
    ctx.stroke();
  }

  ctx.setLineDash([]);
}

function drawSpawnMarker(ctx: CanvasRenderingContext2D, x: number, y: number, cell: number, color: string): void {
  const cx = x * cell + cell / 2;
  const cy = y * cell + cell / 2;
  const size = Math.min(cell * 0.28, 10);

  // 菱形 ◆
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx, cy - size);
  ctx.lineTo(cx + size, cy);
  ctx.lineTo(cx, cy + size);
  ctx.lineTo(cx - size, cy);
  ctx.closePath();
  ctx.fill();

  // 外圈
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, size + 2, 0, Math.PI * 2);
  ctx.stroke();
}

function drawHoverPreview(ctx: CanvasRenderingContext2D, x: number, y: number, cell: number, state: EditorState): void {
  const px = x * cell;
  const py = y * cell;

  ctx.fillStyle = tokenColorRgba("--tactical-cyan", 0.12, "#00E4FF");
  ctx.fillRect(px, py, cell, cell);
  ctx.strokeStyle = TOKEN_MAP.cyan();
  ctx.lineWidth = 1;
  ctx.strokeRect(px + 0.5, py + 0.5, cell - 1, cell - 1);

  // 如果镜像开启，也预览镜像位置
  if (state.hMirror) {
    const mx = state.mapSize - 1 - x;
    if (mx !== x) {
      const mpx = mx * cell;
      ctx.fillStyle = tokenColorRgba("--tactical-cyan", 0.06, "#00E4FF");
      ctx.fillRect(mpx, py, cell, cell);
      ctx.strokeStyle = tokenColorRgba("--tactical-cyan", 0.4, "#00E4FF");
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(mpx + 0.5, py + 0.5, cell - 1, cell - 1);
      ctx.setLineDash([]);
    }
  }
  if (state.vMirror) {
    const my = state.mapSize - 1 - y;
    if (my !== y) {
      const mpy = my * cell;
      ctx.fillStyle = tokenColorRgba("--tactical-cyan", 0.06, "#00E4FF");
      ctx.fillRect(px, mpy, cell, cell);
      ctx.strokeStyle = tokenColorRgba("--tactical-cyan", 0.4, "#00E4FF");
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(px + 0.5, mpy + 0.5, cell - 1, cell - 1);
      ctx.setLineDash([]);
    }
  }
  if (state.hMirror && state.vMirror) {
    const mx = state.mapSize - 1 - x;
    const my = state.mapSize - 1 - y;
    if (mx !== x && my !== y) {
      const mpx = mx * cell;
      const mpy = my * cell;
      ctx.fillStyle = tokenColorRgba("--tactical-cyan", 0.06, "#00E4FF");
      ctx.fillRect(mpx, mpy, cell, cell);
      ctx.strokeStyle = tokenColorRgba("--tactical-cyan", 0.4, "#00E4FF");
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(mpx + 0.5, mpy + 0.5, cell - 1, cell - 1);
      ctx.setLineDash([]);
    }
  }
}

function drawDragPreview(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, cell: number): void {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  ctx.strokeStyle = "#F8B700";
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.strokeRect(
    minX * cell + 0.5,
    minY * cell + 0.5,
    (maxX - minX + 1) * cell - 1,
    (maxY - minY + 1) * cell - 1,
  );
  ctx.setLineDash([]);
}

// ============================================================
// 编辑操作
// ============================================================

/** 应用镜像编辑 */
function applyMirror(tiles: number[][], mapSize: number, x: number, y: number, value: number, hMirror: boolean, vMirror: boolean): void {
  tiles[y][x] = value;
  if (hMirror) {
    const mx = mapSize - 1 - x;
    if (mx >= 0 && mx < mapSize) tiles[y][mx] = value;
  }
  if (vMirror) {
    const my = mapSize - 1 - y;
    if (my >= 0 && my < mapSize) tiles[my][x] = value;
  }
  if (hMirror && vMirror) {
    const mx = mapSize - 1 - x;
    const my = mapSize - 1 - y;
    if (mx >= 0 && mx < mapSize && my >= 0 && my < mapSize) tiles[my][mx] = value;
  }
}

function tileToolToValue(tool: TileTool): number {
  switch (tool) {
    case "empty": return TILE_EMPTY;
    case "cover": return TILE_COVER;
    case "obstacle": return TILE_OBSTACLE;
    case "unreachable": return TILE_UNREACHABLE;
  }
}

function pushUndo(undoStack: UndoSnapshot[], redoStack: RedoStackLike[], state: EditorState, maxUndo: number): void {
  redoStack.length = 0; // 新操作清空 redo
  undoStack.push({
    tiles: state.tiles.map(row => [...row]),
    spawnA: state.spawnA ? { ...state.spawnA } : null,
    spawnB: state.spawnB ? { ...state.spawnB } : null,
  });
  if (undoStack.length > maxUndo) {
    undoStack.shift();
  }
}

type RedoStackLike = UndoSnapshot;

function popUndo(undoStack: UndoSnapshot[], redoStack: RedoStackLike[], state: EditorState): boolean {
  const snapshot = undoStack.pop();
  if (!snapshot) return false;

  redoStack.push({
    tiles: state.tiles.map(row => [...row]),
    spawnA: state.spawnA ? { ...state.spawnA } : null,
    spawnB: state.spawnB ? { ...state.spawnB } : null,
  });

  state.tiles = snapshot.tiles.map(row => [...row]);
  state.spawnA = snapshot.spawnA ? { ...snapshot.spawnA } : null;
  state.spawnB = snapshot.spawnB ? { ...snapshot.spawnB } : null;
  return true;
}

function popRedo(undoStack: UndoSnapshot[], redoStack: RedoStackLike[], state: EditorState): boolean {
  const snapshot = redoStack.pop();
  if (!snapshot) return false;

  undoStack.push({
    tiles: state.tiles.map(row => [...row]),
    spawnA: state.spawnA ? { ...state.spawnA } : null,
    spawnB: state.spawnB ? { ...state.spawnB } : null,
  });

  state.tiles = snapshot.tiles.map(row => [...row]);
  state.spawnA = snapshot.spawnA ? { ...snapshot.spawnA } : null;
  state.spawnB = snapshot.spawnB ? { ...snapshot.spawnB } : null;
  return true;
}

/** 编辑单个 tile（处理工具逻辑） */
function editTile(
  state: EditorState,
  tx: number,
  ty: number,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  maxUndo: number,
): void {
  const { activeTool, mapSize, tiles, hMirror, vMirror } = state;

  if (tx < 0 || tx >= mapSize || ty < 0 || ty >= mapSize) return;

  if (activeTool === "spawn") {
    // 检查是否在中线上
    const midX = Math.floor(mapSize / 2);
    if (tx === midX && mapSize % 2 === 1) {
      showToast("Spawn cannot be placed on the center line (mirror position cannot be generated)", "warn");
      return;
    }
    // 检查是否是空地
    if (tiles[ty][tx] !== TILE_EMPTY) {
      showToast("Spawn can only be placed on empty tiles", "warn");
      return;
    }

    pushUndo(undoStack, redoStack, state, maxUndo);

    // 设置 A spawn
    state.spawnA = { x: tx, y: ty };
    // 自动设置 B spawn（左右镜像）
    const bx = mapSize - 1 - tx;
    state.spawnB = { x: bx, y: ty };

    return;
  }

  if (activeTool === "select") {
    // 选择工具：高亮显示 tile 信息（不修改）
    return;
  }

  if (activeTool === "clear") {
    // 清空工具需要确认弹窗，不在此处理
    return;
  }

  // Tile 工具（empty/cover/obstacle/unreachable）
  const value = tileToolToValue(activeTool);
  pushUndo(undoStack, redoStack, state, maxUndo);
  applyMirror(tiles, mapSize, tx, ty, value, hMirror, vMirror);
}

/** 批量编辑矩形区域 */
function editTileRect(
  state: EditorState,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  maxUndo: number,
): void {
  const { activeTool, mapSize, tiles, hMirror, vMirror } = state;

  if (activeTool === "spawn" || activeTool === "select" || activeTool === "clear") {
    // 批量模式只适用于 tile 工具
    return;
  }

  const minX = Math.max(0, Math.min(x1, x2));
  const maxX = Math.min(mapSize - 1, Math.max(x1, x2));
  const minY = Math.max(0, Math.min(y1, y2));
  const maxY = Math.min(mapSize - 1, Math.max(y1, y2));

  const value = tileToolToValue(activeTool);
  pushUndo(undoStack, redoStack, state, maxUndo);

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      applyMirror(tiles, mapSize, x, y, value, hMirror, vMirror);
    }
  }
}

// ============================================================
// 保存/导出
// ============================================================

function buildLevelData(state: EditorState): LevelData {
  return {
    levelId: state.levelId,
    name: state.levelName,
    difficulty: state.difficulty,
    mapSize: state.mapSize,
    aiName: state.aiName,
    description: state.description,
    isBuiltin: false,
    map: {
      width: state.mapSize,
      height: state.mapSize,
      tiles: state.tiles.map(row => [...row]),
      spawnA: state.spawnA ? [state.spawnA.x, state.spawnA.y] : [0, 0],
      spawnB: state.spawnB ? [state.spawnB.x, state.spawnB.y] : [0, 0],
    },
    playerTemplate: "",
    aiCode: "# Custom AI - placeholder\n",
  };
}

async function doSave(
  state: EditorState,
  levelStore: LevelStore,
  topBarStatusEl: HTMLElement,
  saveBtn: HTMLButtonElement,
  toastContainer: HTMLElement,
  onForked?: (newLevelId: string) => void,
): Promise<void> {
  // 校验 spawn
  if (!state.spawnA || !state.spawnB) {
    showToast("Missing spawn points. Please set spawn points for both teams.", "error", toastContainer);
    return;
  }

  // 镜像对称校验
  if (!state.hMirror) {
    const expectedBx = state.mapSize - 1 - state.spawnA.x;
    if (state.spawnB.x !== expectedBx || state.spawnB.y !== state.spawnA.y) {
      showToast("Spawn points are asymmetric, which may lead to unfair battles", "warn", toastContainer);
    }
  }

  // 禁用按钮
  saveBtn.disabled = true;
  saveBtn.textContent = "[⏳ SAVING...]";
  updateTopBarStatus(topBarStatusEl, "SAVING");

  try {
    // ★ NEW 占位符 / 空 levelId 防御:从 #/editor-map/new 进入时 levelId="new",
    //   或者边角路径下 projectId 变成空串(?? 不兜空串,虽然 main.ts 已用 || 兜底,
    //   但防御性再做一遍),自动 fork 到 _user/user_<ts>
    if (!state.levelId || state.levelId === "new") {
      const newLevelId = `user_${Date.now().toString(36)}`;
      state.levelId = newLevelId;
      state.isBuiltin = false;
      showToast(`🆕 New level created: ${newLevelId}`, "success", toastContainer);
      onForked?.(newLevelId);
    }
    // ★ BUILTIN 关卡保护:不允许直接 save 写 _builtin 目录(后端会 403)
    //   自动 fork 到 _user/<原id>_user_<时间戳>,后续编辑都基于新关卡
    else if (state.isBuiltin) {
      const newLevelId = `${state.levelId}_user_${Date.now().toString(36)}`;
      state.levelId = newLevelId;
      state.isBuiltin = false;
      showToast(`🔀 Built-in levels cannot be saved directly. Forked as new level: ${newLevelId}`, "warn", toastContainer);
      // 通知外部(更新 URL + UI),后续所有 save 都针对新 id
      onForked?.(newLevelId);
    }

    const levelData = buildLevelData(state);
    await levelStore.save(levelData);

    // 更新原始数据（标记为已保存）
    state.originalTiles = state.tiles.map(row => [...row]);
    state.originalSpawnA = state.spawnA ? { ...state.spawnA } : null;
    state.originalSpawnB = state.spawnB ? { ...state.spawnB } : null;

    showToast("✓ Map saved", "success", toastContainer);
    updateTopBarStatus(topBarStatusEl, "SAVED");

    // 3 秒后切回 EDITING
    setTimeout(() => {
      updateTopBarStatus(topBarStatusEl, "EDITING");
    }, 3000);
  } catch (e) {
    showToast(`✗ Save failed: ${(e as Error).message}`, "error", toastContainer);
    updateTopBarStatus(topBarStatusEl, "EDITING");
  } finally {
    saveBtn.disabled = false;
    // ★ 关键:按钮文案随 isBuiltin 状态切换(避免下次再 fork 时显示 SAVE 让用户迷惑)
    saveBtn.textContent = state.isBuiltin ? `[💾 ${t("map_editor.save_as")}]` : `[💾 ${t("map_editor.save")}]`;
  }
}

async function doExport(
  state: EditorState,
  levelStore: LevelStore,
  topBarStatusEl: HTMLElement,
  exportBtn: HTMLButtonElement,
  toastContainer: HTMLElement,
): Promise<void> {
  exportBtn.disabled = true;
  exportBtn.textContent = "[⏳ EXPORTING...]";
  updateTopBarStatus(topBarStatusEl, "EXPORTING");

  try {
    const blob = await levelStore.exportZip(state.levelId);
    downloadLevelZip(state.levelId, blob);
    showToast(`✓ Export success · ${state.levelId}.level.zip`, "success", toastContainer);
  } catch (e) {
    showToast(`✗ Export failed: ${(e as Error).message}`, "error", toastContainer);
  } finally {
    exportBtn.disabled = false;
    exportBtn.textContent = `[📦 ${t("map_editor.export")} .level.zip]`;
    updateTopBarStatus(topBarStatusEl, "EDITING");
  }
}

// ============================================================
// UI 更新函数
// ============================================================

function updateTileCounts(state: EditorState, el: HTMLElement): void {
  let empty = 0, cover = 0, obstacle = 0, unreachable = 0;
  const total = state.mapSize * state.mapSize;

  for (let y = 0; y < state.mapSize; y++) {
    for (let x = 0; x < state.mapSize; x++) {
      switch (state.tiles[y][x]) {
        case TILE_EMPTY: empty++; break;
        case TILE_COVER: cover++; break;
        case TILE_OBSTACLE: obstacle++; break;
        case TILE_UNREACHABLE: unreachable++; break;
      }
    }
  }

  const spawnCount = (state.spawnA ? 1 : 0) + (state.spawnB ? 1 : 0);
  const used = empty + cover + obstacle + unreachable;

  let html = "";
  if (empty > 0) html += `<div class="s7-count-row"><span class="s7-count-icon">▣</span><span class="s7-count-label">Empty</span><span class="s7-count-num">${empty}</span></div>`;
  if (cover > 0) html += `<div class="s7-count-row"><span class="s7-count-icon s7-icon-cover">▤</span><span class="s7-count-label">Cover</span><span class="s7-count-num">${cover}</span></div>`;
  if (obstacle > 0) html += `<div class="s7-count-row"><span class="s7-count-icon s7-icon-obstacle">▥</span><span class="s7-count-label">Obstacle</span><span class="s7-count-num">${obstacle}</span></div>`;
  if (unreachable > 0) html += `<div class="s7-count-row"><span class="s7-count-icon s7-icon-unreachable">▩</span><span class="s7-count-label">Unreachable</span><span class="s7-count-num">${unreachable}</span></div>`;
  if (spawnCount > 0) html += `<div class="s7-count-row"><span class="s7-count-icon s7-icon-spawn">◆</span><span class="s7-count-label">Spawn</span><span class="s7-count-num">${spawnCount}</span></div>`;
  html += `<div class="s7-count-sep"></div>`;
  html += `<div class="s7-count-row s7-count-total"><span class="s7-count-label">Total</span><span class="s7-count-num">${used}/${total}</span></div>`;

  el.innerHTML = html;
}

function updateUndoCount(btn: HTMLButtonElement, count: number, max: number): void {
  if (btn) btn.textContent = `[↶ ${t("map_editor.undo")} ${count}/${max}]`;
}

function updateMirrorUI(hEl: HTMLElement, vEl: HTMLElement, state: EditorState): void {
  if (hEl) {
    hEl.textContent = `[⇅ ${state.hMirror ? "ON" : "OFF"}]`;
    hEl.className = `s7-mirror-btn ${state.hMirror ? "on" : "off"}`;
  }
  if (vEl) {
    vEl.textContent = `[⇄ ${state.vMirror ? "ON" : "OFF"}]`;
    vEl.className = `s7-mirror-btn ${state.vMirror ? "on" : "off"}`;
  }
}

function updateTopBarStatus(el: HTMLElement, status: string): void {
  if (el) el.textContent = `[${status}]`;
}

function getStoragePath(_levelStore: LevelStore, levelId: string): string {
  // DevLevelStore → 真实路径，OPFS → 显示 "OPFS"
  if (import.meta.env.DEV) {
    return `data/levels/custom/${levelId}/`;
  }
  return `OPFS · ${levelId}`;
}

// ============================================================
// Toast 通知
// ============================================================

function showToast(
  message: string,
  type: "success" | "error" | "warn",
  targetContainer?: HTMLElement,
): void {
  const container = targetContainer ?? document.querySelector("#s7-toast-container") as HTMLElement;
  if (!container || !container.isConnected) return;

  const toast = document.createElement("div");
  toast.className = `s7-toast s7-toast-${type}`;
  toast.textContent = message;
  container.appendChild(toast);

  const duration = type === "error" ? 5000 : type === "warn" ? 3000 : 2000;
  setTimeout(() => {
    toast.style.opacity = "0";
    setTimeout(() => toast.remove(), 200);
  }, duration);
}

// ============================================================
// 事件绑定
// ============================================================

function bindToolButtons(root: HTMLElement, state: EditorState, canvas: HTMLCanvasElement | null): void {
  root.querySelectorAll(".s7-tool-btn").forEach(btn => {
    btn.addEventListener("click", () => {
      const tool = (btn as HTMLElement).dataset.tool as EditorTool;
      if (tool === "clear") {
        // 清空工具：弹出确认弹窗
        showClearConfirm(state, root);
        return;
      }
      state.activeTool = tool;

      // 更新工具按钮激活态
      root.querySelectorAll(".s7-tool-btn").forEach(b => b.classList.remove("active"));
      btn.classList.add("active");

      // 更新光标
      if (canvas) {
        canvas.style.cursor = tool === "select" ? "default" : "crosshair";
      }
    });
  });
}

function showClearConfirm(state: EditorState, root: HTMLElement): void {
  // 检查是否已有弹窗
  if (root.querySelector("#s7-clear-dialog")) return;

  const dialog = document.createElement("div");
  dialog.id = "s7-clear-dialog";
  dialog.className = "s7-dialog-overlay";
  dialog.innerHTML = /*html*/`
    <div class="s7-dialog s7-dialog-warn">
      <div class="s7-dialog-title">⚠ Clear all terrain?</div>
      <div class="s7-dialog-body">
        <p>All tiles will be reset to "Empty".</p>
        <p>Spawn points will be removed.</p>
        <p>This action cannot be undone (beyond undo stack).</p>
      </div>
      <div class="s7-dialog-actions">
        <button class="s7-btn s7-btn-ghost" id="s7-clear-cancel">${t("common.cancel")}</button>
        <button class="s7-btn s7-btn-danger" id="s7-clear-confirm">Confirm Clear</button>
      </div>
    </div>
  `;

  root.appendChild(dialog);

  let escHandler: (e: KeyboardEvent) => void;
  let unregisterEsc = () => {};
  const close = () => {
    dialog.remove();
    unregisterEsc();
  };
  dialog.querySelector("#s7-clear-cancel")?.addEventListener("click", close);
  dialog.querySelector("#s7-clear-confirm")?.addEventListener("click", () => {
    initEmptyMap(state);
    close();
    showToast("Map cleared", "warn");
    // 触发重绘
    const canvas = root.querySelector("#s7-canvas") as HTMLCanvasElement;
    if (canvas) {
      const cellSize = calcCellSize(state.mapSize);
      renderEditorCanvasDirect(root, state, cellSize);
    }
    const tileCountsEl = root.querySelector("#s7-tile-counts") as HTMLElement;
    if (tileCountsEl) updateTileCounts(state, tileCountsEl);
    const undoBtn = root.querySelector("#s7-btn-undo") as HTMLButtonElement;
    if (undoBtn) updateUndoCount(undoBtn, 0, 20);
  });

  // Esc 关闭
  escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  unregisterEsc = () => {
    document.removeEventListener("keydown", escHandler);
    screen7DialogEscCleanups.delete(unregisterEsc);
  };
  screen7DialogEscCleanups.add(unregisterEsc);
  document.addEventListener("keydown", escHandler);
}

function bindCanvasEvents(
  root: HTMLElement,
  state: EditorState,
  canvas: HTMLCanvasElement | null,
  canvasRenderer: CanvasRenderer | null,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  maxUndo: number,
  tileCountsEl: HTMLElement,
  topBarStatusEl: HTMLElement,
): void {
  if (!canvas) return;

  let hoverX = -1;
  let hoverY = -1;
  let dragX1 = 0, dragY1 = 0, dragX2 = 0, dragY2 = 0;
  let isDragging = false;
  let lastRenderTime = 0;

  const cellSize = () => calcCellSize(state.mapSize);

  function getTilePos(e: MouseEvent): [number, number] {
    const rect = canvas!.getBoundingClientRect();
    const cs = cellSize();
    const tx = Math.floor((e.clientX - rect.left) / cs);
    const ty = Math.floor((e.clientY - rect.top) / cs);
    return [tx, ty];
  }

  function requestRender(): void {
    const now = performance.now();
    if (now - lastRenderTime < 200) return; // 0.2s 节流
    lastRenderTime = now;

    if (canvasRenderer) {
      renderEditorCanvas(canvasRenderer, state, cellSize(), hoverX, hoverY, dragX1, dragY1, dragX2, dragY2, isDragging);
    }
  }

  canvas.addEventListener("mousemove", (e) => {
    const [tx, ty] = getTilePos(e);
    if (tx !== hoverX || ty !== hoverY) {
      hoverX = tx;
      hoverY = ty;
      if (isDragging) {
        dragX2 = tx;
        dragY2 = ty;
      }
      requestRender();
    }
  });

  canvas.addEventListener("mouseleave", () => {
    hoverX = -1;
    hoverY = -1;
    requestRender();
  });

  canvas.addEventListener("mousedown", (e) => {
    if (e.button !== 0) return; // 只响应左键
    const [tx, ty] = getTilePos(e);
    isDragging = true;
    dragX1 = dragX2 = tx;
    dragY1 = dragY2 = ty;
    requestRender();
  });

  canvas.addEventListener("mouseup", (e) => {
    if (!isDragging) return;
    isDragging = false;

    const [ex, ey] = getTilePos(e);
    dragX2 = ex;
    dragY2 = ey;

    if (Math.abs(dragX2 - dragX1) <= 0 && Math.abs(dragY2 - dragY1) <= 0) {
      // 单击
      editTile(state, dragX1, dragY1, undoStack, redoStack, maxUndo);
    } else {
      // 拖拽批量
      editTileRect(state, dragX1, dragY1, dragX2, dragY2, undoStack, redoStack, maxUndo);
    }

    updateTileCounts(state, tileCountsEl);
    updateUndoCount(root.querySelector("#s7-btn-undo") as HTMLButtonElement, undoStack.length, maxUndo);
    updateTopBarStatus(topBarStatusEl, "EDITING");
    requestRender();
  });

  // 初始渲染
  requestRender();
}

/** 直接渲染（不依赖 CanvasRenderer，用于清空后的重绘） */
function renderEditorCanvasDirect(root: HTMLElement, state: EditorState, cellSize: number): void {
  const canvas = root.querySelector("#s7-canvas") as HTMLCanvasElement;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const { mapSize, tiles, spawnA, spawnB, hMirror, vMirror } = state;
  const cell = cellSize;
  const W = mapSize * cell;
  const H = mapSize * cell;

  // V14 地图提亮
  ctx.fillStyle = TOKEN_MAP.mapBg();
  ctx.fillRect(0, 0, W, H);

  for (let y = 0; y < mapSize; y++) {
    for (let x = 0; x < mapSize; x++) {
      drawEditorTile(ctx, x, y, tiles[y][x], cell);
    }
  }

  drawEditorGrid(ctx, mapSize, cell);
  drawMirrorLines(ctx, mapSize, cell, hMirror, vMirror);

  if (spawnA) drawSpawnMarker(ctx, spawnA.x, spawnA.y, cell, "#00E4FF");
  if (spawnB) drawSpawnMarker(ctx, spawnB.x, spawnB.y, cell, "#FF4655");
}

function bindPanelEvents(
  root: HTMLElement,
  state: EditorState,
  panelEl: HTMLElement,
  panelToggleEl: HTMLElement,
  levelStore: LevelStore,
  tileCountsEl: HTMLElement,
  saveBtn: HTMLButtonElement,
  exportBtn: HTMLButtonElement,
  undoBtn: HTMLButtonElement,
  clearBtn: HTMLButtonElement,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  maxUndo: number,
  topBarStatusEl: HTMLElement,
  toastContainer: HTMLElement,
  onNavigate: (path: string) => void,
): void {
  // 面板折叠
  panelToggleEl?.addEventListener("click", () => {
    panelEl.classList.toggle("collapsed");
    const collapsed = panelEl.classList.contains("collapsed");
    panelToggleEl.textContent = collapsed ? "[▶]" : "[◀]";
  });

  // 关卡名称输入
  const nameInput = root.querySelector("#s7-input-name") as HTMLInputElement;
  nameInput?.addEventListener("input", () => {
    state.levelName = nameInput.value;
    updateTopBarStatus(topBarStatusEl, "EDITING");
  });

  // 描述输入
  const descInput = root.querySelector("#s7-input-desc") as HTMLTextAreaElement;
  descInput?.addEventListener("input", () => {
    state.description = descInput.value;
  });

  // 难度选择
  const diffSelect = root.querySelector("#s7-select-difficulty") as HTMLSelectElement;
  diffSelect?.addEventListener("change", () => {
    state.difficulty = diffSelect.value as "easy" | "medium" | "hard";
  });

  // 保存
  saveBtn?.addEventListener("click", () => {
    const oldLevelId = state.levelId;  // ★ FR-025:fork 前捕获原始 levelId
    doSave(state, levelStore, topBarStatusEl, saveBtn, toastContainer, (newLevelId) => {
      // ★ FR-025(2026-07-08):fork 后重定向所有关联 Project 的 levelId/mapRef,
      //   确保屏 4 加载的是新关卡而非原始内置地图
      if (oldLevelId !== newLevelId) {
        redirectProjectLevel(oldLevelId, newLevelId);
      }
      // BUILTIN 自动 fork:更新 URL + 重新渲染 storagePath
      onNavigate(`#/editor-map/${newLevelId}`);
    });
  });

  // 导出
  exportBtn?.addEventListener("click", () => {
    doExport(state, levelStore, topBarStatusEl, exportBtn, toastContainer);
  });

  // 撤销
  undoBtn?.addEventListener("click", () => {
    if (popUndo(undoStack, redoStack, state)) {
      updateTileCounts(state, tileCountsEl);
      updateUndoCount(undoBtn, undoStack.length, maxUndo);
      updateTopBarStatus(topBarStatusEl, "EDITING");

      const cellSize = calcCellSize(state.mapSize);
      renderEditorCanvasDirect(root, state, cellSize);
    }
  });

  // 清空
  clearBtn?.addEventListener("click", () => {
    showClearConfirm(state, root);
  });

  // 返回
  root.querySelector("#s7-btn-back")?.addEventListener("click", () => {
    onNavigate(pathFor("armory-hall"));
  });
}

function bindTopBarEvents(
  root: HTMLElement,
  state: EditorState,
  canvas: HTMLCanvasElement | null,
  _canvasRenderer: CanvasRenderer | null,
  mirrorHEl: HTMLElement,
  mirrorVEl: HTMLElement,
  sizeSelectEl: HTMLSelectElement,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  _toastContainer: HTMLElement,
  topBarStatusEl: HTMLElement,
  tileCountsEl: HTMLElement,
): void {
  // 镜像开关
  mirrorHEl?.addEventListener("click", () => {
    state.hMirror = !state.hMirror;
    updateMirrorUI(mirrorHEl, mirrorVEl, state);
    if (!state.hMirror) {
      showToast("⚠ Mirror disabled · map may violate symmetry rules. Keeping mirror on is recommended.", "warn");
    }
    const cellSize = calcCellSize(state.mapSize);
    renderEditorCanvasDirect(root, state, cellSize);
  });

  mirrorVEl?.addEventListener("click", () => {
    state.vMirror = !state.vMirror;
    updateMirrorUI(mirrorHEl, mirrorVEl, state);
    if (!state.vMirror) {
      showToast("⚠ Mirror disabled · map may violate symmetry rules. Keeping mirror on is recommended.", "warn");
    }
    const cellSize = calcCellSize(state.mapSize);
    renderEditorCanvasDirect(root, state, cellSize);
  });

  // 尺寸切换
  sizeSelectEl?.addEventListener("change", () => {
    const newSize = parseInt(sizeSelectEl.value) as MapSize;
    if (newSize === state.mapSize) return;

    showSizeChangeDialog(root, state, newSize, undoStack, redoStack, canvas, tileCountsEl, topBarStatusEl);
  });
}

function showSizeChangeDialog(
  root: HTMLElement,
  state: EditorState,
  newSize: MapSize,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  canvas: HTMLCanvasElement | null,
  tileCountsEl: HTMLElement,
  topBarStatusEl: HTMLElement,
): void {
  if (root.querySelector("#s7-size-dialog")) return;

  const dialog = document.createElement("div");
  dialog.id = "s7-size-dialog";
  dialog.className = "s7-dialog-overlay";
  dialog.innerHTML = /*html*/`
    <div class="s7-dialog s7-dialog-warn">
      <div class="s7-dialog-title">⚠ Change map size?</div>
      <div class="s7-dialog-body">
        <p>Current: ${state.mapSize}×${state.mapSize} → Target: ${newSize}×${newSize}</p>
        <p>After switching:</p>
        <ul>
          <li>Overlapping area (${Math.min(state.mapSize, newSize)}×${Math.min(state.mapSize, newSize)}) terrain preserved</li>
          <li>New areas filled as "Empty"</li>
          <li>Spawn points preserved if within overlapping area</li>
          <li>Undo stack will be cleared</li>
        </ul>
      </div>
      <div class="s7-dialog-actions">
        <button class="s7-btn s7-btn-ghost" id="s7-size-cancel">${t("common.cancel")}</button>
        <button class="s7-btn s7-btn-warn" id="s7-size-confirm">Confirm Switch</button>
      </div>
    </div>
  `;

  root.appendChild(dialog);

  let escHandler: (e: KeyboardEvent) => void;
  let unregisterEsc = () => {};
  const close = () => {
    dialog.remove();
    unregisterEsc();
  };
  dialog.querySelector("#s7-size-cancel")?.addEventListener("click", () => {
    close();
    // 恢复 select 值
    const selectEl = root.querySelector("#s7-size-select") as HTMLSelectElement;
    if (selectEl) selectEl.value = String(state.mapSize);
  });

  dialog.querySelector("#s7-size-confirm")?.addEventListener("click", () => {
    // 迁移数据
    const oldSize = state.mapSize;
    const oldTiles = state.tiles;
    const oldSpawnA = state.spawnA;
    const oldSpawnB = state.spawnB;

    state.mapSize = newSize;
    state.tiles = [];
    for (let y = 0; y < newSize; y++) {
      state.tiles[y] = new Array(newSize).fill(TILE_EMPTY);
    }

    // 复制重叠区域
    const overlap = Math.min(oldSize, newSize);
    for (let y = 0; y < overlap; y++) {
      for (let x = 0; x < overlap; x++) {
        state.tiles[y][x] = oldTiles[y][x];
      }
    }

    // spawn 处理
    if (oldSpawnA && oldSpawnA.x < newSize && oldSpawnA.y < newSize) {
      state.spawnA = { ...oldSpawnA };
    } else {
      state.spawnA = null;
    }
    if (oldSpawnB && oldSpawnB.x < newSize && oldSpawnB.y < newSize) {
      state.spawnB = { ...oldSpawnB };
    } else {
      state.spawnB = null;
    }

    // 清空撤销栈
    undoStack.length = 0;
    redoStack.length = 0;

    // 更新 Canvas
    if (canvas) {
      const cellSize = calcCellSize(newSize);
      canvas.width = newSize * cellSize;
      canvas.height = newSize * cellSize;
    }

    updateTileCounts(state, tileCountsEl);
    updateUndoCount(root.querySelector("#s7-btn-undo") as HTMLButtonElement, 0, 20);
    updateTopBarStatus(topBarStatusEl, "EDITING");

    const cellSize = calcCellSize(newSize);
    renderEditorCanvasDirect(root, state, cellSize);

    // 更新坐标标注
    updateCoordLabels(root, newSize);

    close();

    if (oldSpawnA && !state.spawnA) {
      showToast("Spawn was clipped, please reset", "warn");
    }
  });

  escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      close();
      const selectEl = root.querySelector("#s7-size-select") as HTMLSelectElement;
      if (selectEl) selectEl.value = String(state.mapSize);
    }
  };
  unregisterEsc = () => {
    document.removeEventListener("keydown", escHandler);
    screen7DialogEscCleanups.delete(unregisterEsc);
  };
  screen7DialogEscCleanups.add(unregisterEsc);
  document.addEventListener("keydown", escHandler);
}

function updateCoordLabels(root: HTMLElement, size: number): void {
  const topEl = root.querySelector("#s7-coords-top");
  const bottomEl = root.querySelector("#s7-coords-bottom");
  const leftEl = root.querySelector("#s7-coords-left");
  const rightEl = root.querySelector("#s7-coords-right");

  if (topEl) topEl.innerHTML = buildCoordLabels(size);
  if (bottomEl) bottomEl.innerHTML = buildCoordLabels(size);
  if (leftEl) leftEl.innerHTML = buildCoordLabelsY(size);
  if (rightEl) rightEl.innerHTML = buildCoordLabelsY(size);
}

function bindKeyboardEvents(
  root: HTMLElement,
  state: EditorState,
  canvas: HTMLCanvasElement | null,
  _canvasRenderer: CanvasRenderer | null,
  undoStack: UndoSnapshot[],
  redoStack: RedoStackLike[],
  maxUndo: number,
  tileCountsEl: HTMLElement,
  topBarStatusEl: HTMLElement,
  levelStore: LevelStore,
  toastContainer: HTMLElement,
  saveBtn: HTMLButtonElement,
  onNavigate: (path: string) => void,
): () => void {
  const keyHandler = (e: KeyboardEvent) => {
    // 如果焦点在 input/textarea/select 中，不处理快捷键
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

    const key = e.key;
    const ctrl = e.ctrlKey || e.metaKey;

    // Ctrl+S / Cmd+S → 保存
    if (ctrl && key === "s") {
      e.preventDefault();
      doSave(state, levelStore, topBarStatusEl, saveBtn, toastContainer, (newLevelId) => {
        onNavigate(`#/editor-map/${newLevelId}`);
      });
      return;
    }

    // Ctrl+Z / Cmd+Z → 撤销
    if (ctrl && !e.shiftKey && key === "z") {
      e.preventDefault();
      if (popUndo(undoStack, redoStack, state)) {
        updateTileCounts(state, tileCountsEl);
        updateUndoCount(root.querySelector("#s7-btn-undo") as HTMLButtonElement, undoStack.length, maxUndo);
        updateTopBarStatus(topBarStatusEl, "EDITING");
        const cellSize = calcCellSize(state.mapSize);
        renderEditorCanvasDirect(root, state, cellSize);
      }
      return;
    }

    // Ctrl+Shift+Z / Cmd+Shift+Z → 重做
    if (ctrl && e.shiftKey && key === "Z") {
      e.preventDefault();
      if (popRedo(undoStack, redoStack, state)) {
        updateTileCounts(state, tileCountsEl);
        updateUndoCount(root.querySelector("#s7-btn-undo") as HTMLButtonElement, undoStack.length, maxUndo);
        updateTopBarStatus(topBarStatusEl, "EDITING");
        const cellSize = calcCellSize(state.mapSize);
        renderEditorCanvasDirect(root, state, cellSize);
      }
      return;
    }

    // 数字键 1-8 切换工具
    const toolMap: Record<string, EditorTool> = {
      "1": "empty",
      "2": "cover",
      "3": "obstacle",
      "4": "unreachable",
      "5": "spawn",
      "6": "spawn",
      "7": "select",
      "8": "clear",
    };

    if (key in toolMap) {
      const tool = toolMap[key];
      if (tool === "clear") {
        showClearConfirm(state, root);
        return;
      }
      state.activeTool = tool;
      root.querySelectorAll(".s7-tool-btn").forEach(b => b.classList.remove("active"));
      const btn = root.querySelector(`.s7-tool-btn[data-tool="${tool}"]`);
      if (btn) btn.classList.add("active");
      if (canvas) {
        canvas.style.cursor = tool === "select" ? "default" : "crosshair";
      }
      return;
    }

    // Esc 取消工具
    if (key === "Escape") {
      state.activeTool = "empty";
      root.querySelectorAll(".s7-tool-btn").forEach(b => b.classList.remove("active"));
      const emptyBtn = root.querySelector('.s7-tool-btn[data-tool="empty"]');
      if (emptyBtn) emptyBtn.classList.add("active");
      if (canvas) canvas.style.cursor = "crosshair";
      return;
    }
  };
  document.addEventListener("keydown", keyHandler);
  return () => document.removeEventListener("keydown", keyHandler);
}

// ============================================================
// 工具函数
// ============================================================

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
